import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Camera, ArrowLeft, Check, X, Eye } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

export default function ModeratorPage() {
  const navigate = useNavigate();
  const [pendingStories, setPendingStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPendingStories();
  }, []);

  const loadPendingStories = async () => {
    try {
      const res = await axios.get(`${API}/stories/pending`);
      setPendingStories(res.data);
    } catch (error) {
      toast.error('Failed to load pending stories');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (storyId) => {
    try {
      await axios.put(`${API}/stories/${storyId}/approve`);
      toast.success('Story approved!');
      loadPendingStories();
    } catch (error) {
      toast.error('Failed to approve story');
    }
  };

  const handleReject = async (storyId) => {
    try {
      await axios.put(`${API}/stories/${storyId}/reject`);
      toast.success('Story rejected');
      loadPendingStories();
    } catch (error) {
      toast.error('Failed to reject story');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100">
      {/* Navigation */}
      <nav className="glass fixed top-0 left-0 right-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/')}
                data-testid="back-home-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
              <div className="flex items-center space-x-2">
                <Camera className="w-8 h-8 text-purple-600" />
                <span className="text-2xl font-bold text-slate-800">Moderator Dashboard</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-slate-800 mb-2">Story Moderation</h1>
            <p className="text-slate-600">
              Review and approve pending stories from the community.
            </p>
          </div>

          {loading ? (
            <Card className="p-12 text-center">
              <p className="text-slate-600">Loading...</p>
            </Card>
          ) : pendingStories.length === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-slate-600 text-lg">No pending stories to review.</p>
            </Card>
          ) : (
            <div className="space-y-6" data-testid="pending-stories">
              {pendingStories.map(story => (
                <Card key={story.id} className="p-6" data-testid={`pending-story-${story.id}`}>
                  <div className="flex flex-col md:flex-row gap-6">
                    {story.image_url && (
                      <div className="md:w-64 h-64 flex-shrink-0">
                        <img 
                          src={story.image_url} 
                          alt={story.title} 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                          Pending Review
                        </span>
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          {story.category}
                        </span>
                        {story.tags.map(tag => (
                          <span key={tag} className="text-xs text-slate-500">#{tag}</span>
                        ))}
                      </div>
                      <h2 className="text-2xl font-bold text-slate-800 mb-2">{story.title}</h2>
                      <p className="text-slate-600 mb-4">{story.content}</p>
                      <div className="text-sm text-slate-500 mb-4">
                        <span>By {story.author_email}</span>
                        <span className="mx-2">•</span>
                        <span>{new Date(story.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-3">
                        <Button 
                          onClick={() => handleApprove(story.id)}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`approve-btn-${story.id}`}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                        <Button 
                          onClick={() => handleReject(story.id)}
                          variant="destructive"
                          data-testid={`reject-btn-${story.id}`}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                        <Button 
                          onClick={() => navigate(`/stories/${story.id}`)}
                          variant="outline"
                          data-testid={`view-btn-${story.id}`}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Full Story
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}